import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { MissingChapterContext } from '../src/types/missingChapterContext';
import type { RecoveryContract, ValidationReport } from '../src/types';
import { RecoveryContractView } from './RecoveryContractView';
import {
  approveReconstructionClient,
  approveRecoveryContractClient,
  cancelRecoveryOperationClient,
  compileRecoveryContractClient,
  reconstructChapterClient,
  restoreReconstructedChapter,
  saveRecoveryContractClient,
  validateReconstructedProseClient,
} from '../src/utils/recoveryTransactions';

type ReconstructStep =
  | 'INTRO'
  | 'CONTRACT'
  | 'PROSE'
  | 'REVIEW';

interface ReconstructPanelProps {
  projectId: string;
  targetChapter: number;
  context: MissingChapterContext | null;
  onRecovered?: () => void;
  onError: (message: string) => void;
  disabled?: boolean;
}

function ProgressLabel({ phase }: { phase: string | null }) {
  if (!phase) return null;
  const labels: Record<string, string> = {
    contract: 'Preparing story context…',
    prose: 'Building replacement chapter…',
    validate: 'Checking continuity…',
    approval: 'Recording your approval…',
    restore: 'Restoring reconstructed chapter…',
  };
  return <p className="text-sm text-cv-muted animate-pulse">{labels[phase] ?? phase}</p>;
}

function hasBlockingViolations(v: ValidationReport | null): boolean {
  if (!v) return true;
  if (!v.passed) return true;
  return v.violations.some((x) => x.severity === 'BLOCKING');
}

export function ReconstructPanel({
  projectId,
  targetChapter,
  context,
  onRecovered,
  onError,
  disabled,
}: ReconstructPanelProps) {
  const [step, setStep] = useState<ReconstructStep>('INTRO');
  const [operationId, setOperationId] = useState<string | null>(null);
  const [contract, setContract] = useState<RecoveryContract | null>(null);
  const [contractHash, setContractHash] = useState<string | null>(null);
  const [contractApproved, setContractApproved] = useState(false);
  const [progressPhase, setProgressPhase] = useState<string | null>(null);
  const [prose, setProse] = useState<string | null>(null);
  const [proseTitle, setProseTitle] = useState('');
  const [wordCount, setWordCount] = useState(0);
  const [validation, setValidation] = useState<ValidationReport | null>(null);
  const [validationStatus, setValidationStatus] = useState<string | null>(null);
  const [confirmRestore, setConfirmRestore] = useState(false);
  const [busy, setBusy] = useState(false);
  const [localError, setLocalError] = useState('');

  const sourceChapterNumbers = useMemo(
    () => [targetChapter - 1, targetChapter + 1].filter((n) => n >= 1),
    [targetChapter],
  );

  const resetWorkflow = useCallback(() => {
    setStep('INTRO');
    setOperationId(null);
    setContract(null);
    setContractHash(null);
    setContractApproved(false);
    setProse(null);
    setProseTitle('');
    setWordCount(0);
    setValidation(null);
    setValidationStatus(null);
    setConfirmRestore(false);
    setLocalError('');
    setProgressPhase(null);
  }, []);

  useEffect(() => {
    resetWorkflow();
  }, [projectId, targetChapter, resetWorkflow]);

  const ensureOperationId = () => {
    if (operationId) return operationId;
    const id = crypto.randomUUID();
    setOperationId(id);
    return id;
  };

  const handleBuildPlan = async () => {
    setBusy(true);
    setLocalError('');
    setProgressPhase('contract');
    try {
      const opId = ensureOperationId();
      const result = await compileRecoveryContractClient(projectId, targetChapter, opId);
      setContract(result.contract);
      setContractApproved(false);
      setContractHash(null);
      setStep('CONTRACT');
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Failed to build recovery plan';
      setLocalError(msg);
      onError(msg);
    } finally {
      setBusy(false);
      setProgressPhase(null);
    }
  };

  const handleSaveContract = async () => {
    if (!contract || !operationId) return;
    setBusy(true);
    setLocalError('');
    try {
      const result = await saveRecoveryContractClient(projectId, targetChapter, operationId, contract);
      setContract(result.contract);
      setContractApproved(false);
      setContractHash(null);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Failed to save contract';
      setLocalError(msg);
      onError(msg);
    } finally {
      setBusy(false);
    }
  };

  const handleApproveContract = async () => {
    if (!contract || !operationId) return;
    setBusy(true);
    setLocalError('');
    try {
      const result = await approveRecoveryContractClient(projectId, targetChapter, operationId, contract);
      setContract(result.contract);
      setContractHash(result.contractHash);
      setContractApproved(true);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Failed to approve contract';
      setLocalError(msg);
      onError(msg);
    } finally {
      setBusy(false);
    }
  };

  const handleGenerateProse = async (forceRegenerate = false) => {
    if (!operationId || !contractApproved || !contractHash) {
      setLocalError('Approve the recovery plan on the server before generating prose.');
      return;
    }
    setBusy(true);
    setLocalError('');
    setProgressPhase('prose');
    setValidation(null);
    setValidationStatus(null);
    setConfirmRestore(false);
    try {
      const result = await reconstructChapterClient(projectId, targetChapter, operationId, {
        contractHash,
        forceRegenerate,
      });
      setProse(result.prose);
      setProseTitle(result.title || contract?.requirements?.[0]?.description?.slice(0, 80) || `Chapter ${targetChapter}`);
      setWordCount(result.wordCount);
      setStep('PROSE');
      setProgressPhase('validate');
      const val = await validateReconstructedProseClient(projectId, targetChapter, operationId, contractHash);
      setValidation(val.validation);
      setValidationStatus(val.status);
      setStep('REVIEW');
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Reconstruction failed';
      setLocalError(msg);
      onError(msg);
    } finally {
      setBusy(false);
      setProgressPhase(null);
    }
  };

  const handleRevalidate = async () => {
    if (!operationId || !contractHash) return;
    setBusy(true);
    setLocalError('');
    setProgressPhase('validate');
    try {
      const val = await validateReconstructedProseClient(projectId, targetChapter, operationId, contractHash);
      setValidation(val.validation);
      setValidationStatus(val.status);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Validation failed';
      setLocalError(msg);
      onError(msg);
    } finally {
      setBusy(false);
      setProgressPhase(null);
    }
  };

  const handleRestore = async () => {
    if (!operationId || !prose || !contractHash || !contract || !confirmRestore) return;
    if (hasBlockingViolations(validation)) {
      setLocalError('Resolve blocking validation issues before restoring.');
      return;
    }
    if (validationStatus !== 'READY_FOR_RESTORE') {
      setLocalError('Validation must complete without blocking violations.');
      return;
    }
    setBusy(true);
    setLocalError('');
    setProgressPhase('approval');
    try {
      const approval = await approveReconstructionClient(projectId, targetChapter, operationId);
      if (approval.status !== 'RESTORE_APPROVED') {
        throw new Error('Final approval was not recorded server-side.');
      }
      setProgressPhase('restore');
      await restoreReconstructedChapter(projectId, targetChapter, operationId);
      onRecovered?.();
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Restore failed';
      setLocalError(msg);
      onError(msg);
    } finally {
      setBusy(false);
      setProgressPhase(null);
    }
  };

  const handleCancel = async () => {
    if (operationId) {
      try {
        await cancelRecoveryOperationClient(projectId, operationId);
      } catch {
        /* non-fatal */
      }
    }
    resetWorkflow();
  };

  if (disabled) {
    return <p className="text-cv-muted text-sm">Reconstruction is unavailable for this project.</p>;
  }

  return (
    <div className="space-y-4">
      <div className="rounded-md border border-cv-interactive/30 bg-cv-interactive-soft p-3 text-sm text-cv-primary">
        <p className="font-medium">Reconstruct a replacement chapter</p>
        <p className="text-xs text-cv-secondary mt-1">
          This creates new prose from surrounding context — not the original missing chapter.
        </p>
      </div>

      {context && step === 'INTRO' && (
        <p className="text-xs text-cv-muted">
          Context loaded for Chapter {targetChapter}. Build a recovery plan before generating prose.
        </p>
      )}

      <ProgressLabel phase={progressPhase} />

      {step === 'INTRO' && (
        <button
          type="button"
          disabled={busy}
          onClick={handleBuildPlan}
          className="w-full py-2.5 bg-cv-interactive hover:bg-cv-interactive-hover text-cv-page rounded text-sm font-semibold disabled:opacity-50"
        >
          {busy ? 'Building plan…' : 'Build Recovery Plan'}
        </button>
      )}

      {step === 'CONTRACT' && contract && (
        <div className="space-y-4">
          <RecoveryContractView contract={contract} onChange={setContract} />
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              disabled={busy}
              onClick={handleSaveContract}
              className="flex-1 min-w-[8rem] py-2 border border-cv-border-strong rounded text-sm text-cv-primary disabled:opacity-50"
            >
              Save Changes
            </button>
            <button
              type="button"
              disabled={busy || contractApproved}
              onClick={handleApproveContract}
              className="flex-1 min-w-[8rem] py-2 bg-green-700 hover:bg-green-600 text-cv-primary rounded text-sm font-semibold disabled:opacity-50"
            >
              {contractApproved ? 'Plan Approved' : 'Approve Recovery Plan'}
            </button>
            <button type="button" disabled={busy} onClick={handleCancel} className="py-2 px-4 text-sm text-cv-muted">
              Cancel
            </button>
          </div>
          {contractApproved && (
            <button
              type="button"
              disabled={busy}
              onClick={() => handleGenerateProse(false)}
              className="w-full py-2.5 bg-cv-interactive hover:bg-cv-interactive-hover text-cv-page rounded text-sm font-semibold disabled:opacity-50"
            >
              Generate Replacement Chapter
            </button>
          )}
        </div>
      )}

      {(step === 'PROSE' || step === 'REVIEW') && prose && (
        <div className="space-y-4">
          <div className="rounded border border-amber-900/50 bg-amber-950/30 p-3 text-cv-warning text-sm font-medium">
            This is a reconstructed replacement, not the original missing chapter.
          </div>

          <div className="flex flex-wrap gap-3 text-xs text-cv-muted">
            <span>Title: {proseTitle}</span>
            <span>{wordCount} words</span>
            {validation && (
              <>
                <span>Continuity: {validation.continuityScore}/100</span>
                <span>Style: {validation.styleScore}/100</span>
                <span>Opening bridge: {validation.openingBridgePassed ? '✓' : '✗'}</span>
                <span>Closing bridge: {validation.closingBridgePassed ? '✓' : '✗'}</span>
              </>
            )}
          </div>

          <div className="max-h-48 overflow-y-auto rounded border border-cv-border-strong bg-cv-page p-3 text-sm text-cv-secondary font-serif whitespace-pre-wrap leading-relaxed">
            {prose}
          </div>

          {validation?.violations?.length ? (
            <div className="space-y-2">
              {validation.violations.map((v, i) => (
                <div
                  key={i}
                  className={`text-xs rounded p-2 border ${
                    v.severity === 'BLOCKING'
                      ? 'border-red-900/50 bg-red-950/30 text-red-200'
                      : 'border-amber-900/40 bg-cv-warning/10 text-cv-warning'
                  }`}
                >
                  <p className="font-medium">{v.severity}: {v.description}</p>
                  {v.evidence?.map((e, j) => (
                    <p key={j} className="opacity-80 mt-0.5">• {e}</p>
                  ))}
                </div>
              ))}
            </div>
          ) : null}

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                setStep('CONTRACT');
                setContractApproved(true);
              }}
              className="py-2 px-3 border border-cv-border-strong rounded text-xs text-cv-secondary"
            >
              Revise Recovery Plan
            </button>
            <button
              type="button"
              disabled={busy || !contractApproved}
              onClick={() => handleGenerateProse(true)}
              className="py-2 px-3 border border-cv-border-strong rounded text-xs text-cv-secondary"
            >
              Regenerate Chapter
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={handleRevalidate}
              className="py-2 px-3 border border-cv-border-strong rounded text-xs text-cv-secondary"
            >
              Review Again
            </button>
          </div>

          <label className="flex items-start gap-2 text-sm text-cv-secondary">
            <input
              type="checkbox"
              checked={confirmRestore}
              onChange={(e) => setConfirmRestore(e.target.checked)}
              disabled={busy || hasBlockingViolations(validation)}
              className="mt-0.5"
            />
            <span>
              I reviewed this reconstructed chapter and approve restoring it to my manuscript.
            </span>
          </label>

          <div className="flex gap-2">
            <button type="button" disabled={busy} onClick={handleCancel} className="flex-1 py-2 border border-cv-border-strong rounded text-sm">
              Cancel
            </button>
            <button
              type="button"
              disabled={
                busy ||
                !confirmRestore ||
                !contractApproved ||
                !prose ||
                !validation ||
                hasBlockingViolations(validation) ||
                validationStatus !== 'READY_FOR_RESTORE'
              }
              onClick={handleRestore}
              className="flex-1 py-2 bg-green-600 hover:bg-green-500 text-cv-primary rounded text-sm font-semibold disabled:opacity-50"
            >
              {busy
                ? progressPhase === 'approval'
                  ? 'Recording approval…'
                  : progressPhase === 'restore'
                    ? 'Restoring…'
                    : 'Working…'
                : 'Approve and Restore'}
            </button>
          </div>
        </div>
      )}

      {localError && (
        <div className="p-3 bg-red-950/50 border border-red-900/50 rounded text-cv-danger text-sm">{localError}</div>
      )}
    </div>
  );
}
